// ===== ENTRY POINT =====
//
// Boot flow for the Gallery app:
//   1. Resolve the event slug from the URL. No slug -> "invalid link" screen.
//   2. PIN gate: if a (non-expired) token is already stored for this slug, try
//      it; otherwise prompt for the 6-digit PIN -> verify-pin -> token.
//   3. With a valid token, load the event's approved submissions and hand off
//      to GuestBook to run the cover / intro / wall experience.
//
// On any 401 while loading (token expired/invalid), we drop back to the PIN
// gate with a gentle re-enter message. The slug is the only thing that scopes
// the gallery to a couple, and the token is event-scoped — so the gallery can
// only ever show this event's approved data.

import './styles/style.scss';
import { GuestBook } from './gallery.js';
import { resolveSlug } from './config.js';
import {
    verifyPin,
    getToken,
    clearToken,
    ApiError,
} from './api.js';
import { loadGallery } from './data.js';
import { mockSubmissions } from './mock-data.js';

// Dev-only: skip the slug/PIN/API and boot straight from bundled mock data so
// the layout can be checked (mobile etc.) without a real event. Active only
// under `npm run dev` (never in `npm run build`), and only when not explicitly
// disabled with `?live=1` / `?real=1` for testing the real flow in dev.
function devMockEnabled() {
    if (!import.meta.env.DEV) return false;
    const params = new URLSearchParams(window.location.search);
    if (params.has('live') || params.has('real')) return false;
    return true;
}

const els = {
    invalid: () => document.getElementById('invalid'),
    invalidMessage: () => document.getElementById('invalid-message'),
    gate: () => document.getElementById('pin-gate'),
    form: () => document.getElementById('pin-form'),
    input: () => document.getElementById('pin-input'),
    submit: () => document.getElementById('pin-submit'),
    error: () => document.getElementById('pin-error'),
    couple: () => document.getElementById('pin-couple'),
    cover: () => document.getElementById('cover'),
};

let slug = null;

function showInvalid(message) {
    const el = els.invalid();
    if (message) els.invalidMessage().textContent = message;
    el.hidden = false;
    document.body.classList.add('is-locked');
}

function showGate(message) {
    const gate = els.gate();
    gate.hidden = false;
    document.body.classList.add('is-locked');
    if (message) setGateError(message);
    // Defer focus until the element is actually visible.
    requestAnimationFrame(() => els.input()?.focus());
}

function hideGate() {
    els.gate().hidden = true;
}

function setGateError(message) {
    els.error().textContent = message || '';
}

function setGateBusy(busy) {
    els.submit().disabled = busy;
    els.input().disabled = busy;
    els.submit().classList.toggle('is-busy', busy);
}

// Populate the couple's name on the PIN gate once we know it (after a
// successful load). Before that, the gate shows a neutral placeholder.
function setGateCouple(name) {
    if (name) els.couple().textContent = name;
}

// Boot the full gallery experience with loaded data.
function startGallery({ event, submissions }) {
    hideGate();
    // GuestBook reads everything it needs from these; the slug stays the
    // scoping key but the UI itself is data-driven from here.
    window.guestBook = new GuestBook({ slug, event, submissions });
}

// Attempt a load with the given token. Returns true on success.
// On 401 it clears the stored token and returns false (caller shows the gate).
async function tryLoad(token) {
    try {
        const result = await loadGallery(slug, token);
        if (result.event?.coupleName) setGateCouple(result.event.coupleName);
        startGallery(result);
        return true;
    } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
            clearToken(slug);
            return false;
        }
        if (err instanceof ApiError && err.status === 404) {
            showInvalid(err.message);
            return true; // handled (terminal) — don't fall through to the gate
        }
        // Network or 5xx — surface it on the gate so the user can retry.
        showGate();
        setGateError(
            err instanceof ApiError
                ? err.message
                : 'Could not reach the gallery. Please check your connection and try again.'
        );
        return true; // handled
    }
}

function attachGate() {
    const form = els.form();
    const input = els.input();

    // Numeric-only, max 6 — keep the field clean as they type.
    input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 6);
        setGateError('');
    });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const pin = input.value.trim();
        if (pin.length !== 6) {
            setGateError('Please enter all 6 digits.');
            return;
        }

        setGateBusy(true);
        setGateError('');
        try {
            const token = await verifyPin(slug, pin);
            // PIN ok — load the gallery. A fresh token shouldn't 401, but if it
            // somehow does, tryLoad will re-show the gate.
            const handled = await tryLoad(token);
            if (!handled) {
                setGateError('Your session expired. Please enter the PIN again.');
                input.value = '';
            }
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.status === 404) {
                    showInvalid(err.message);
                } else {
                    // 401 (Incorrect PIN), 429 (rate-limited), 5xx, etc.
                    setGateError(err.message);
                    if (err.status === 401) input.value = '';
                }
            } else {
                setGateError('Could not reach the server. Please try again.');
            }
            input.focus();
        } finally {
            setGateBusy(false);
        }
    });
}

async function start() {
    // Dev layout mode: bypass the gate entirely and render dummy submissions.
    if (devMockEnabled()) {
        slug = resolveSlug() || 'dev-preview';
        startGallery({
            event: { coupleName: 'Lena & Farrell', weddingDate: '2026-09-12' },
            submissions: mockSubmissions.map((s) => ({ ...s, photoUrl: null, audioUrl: null })),
        });
        return;
    }

    slug = resolveSlug();

    if (!slug) {
        showInvalid();
        return;
    }

    attachGate();

    // If we already hold a token for this slug (refresh within the hour), try
    // it silently; the server is the source of truth on expiry (401 -> gate).
    const stored = getToken(slug);
    if (stored) {
        const handled = await tryLoad(stored);
        if (handled) return; // gallery shown, invalid screen, or gate already up
    }

    showGate();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
} else {
    start();
}
